import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useReducer,
  Dispatch,
  useCallback,
} from 'react';
import { Song, Video } from '../interfaces/userInterfaces';
import { fetchLatestSongs } from '../services/fetch-utils';
import { fetchLatestVideos } from '../services/fetch-utils';

type PublicDataType = {
  latestVideos: Video[];
  latestSongs: Song[];
  loadReady: boolean;
  state: { count: number };
  dispatch: Dispatch<counterAction>;
  isVideoArray: (value: Video[]) => boolean;
  isSongArray: (value: Song[]) => boolean;
};
type CounterState = { count: number };

type counterAction = { type: 'increment' };

export const PublicDataContext = createContext<PublicDataType>({} as PublicDataType);

export const PublicDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [loadReady, setLoadReady] = useState(false);
  const [latestVideos, setLatestVideos] = useState<Video[]>([
    {
      created_at: '',
      description: '',
      id: 0,
      title: '',
      username: '',
      video: '',
      video_name: '',
      video_id: 0,
      views: 0,
    },
  ]);

  const [latestSongs, setLatestSongs] = useState<Song[]>([
    {
      created_at: '',
      description: '',
      media: '',
      song_id: 0,
      title: '',
      user_id: '',
      username: '',
    },
  ]);

  const counterReducer = (state: CounterState, action: counterAction): CounterState => {
    if (action.type === 'increment' && state.count <= latestVideos.length) {
      return { count: state.count + 1 };
    } else if (state.count > latestVideos.length) {
      return { count: 0 };
    } else {
      return state;
    }
  };

  const [state, dispatch] = useReducer(counterReducer, { count: 0 });

  const isVideoArray = (value: Video[]): value is Video[] => {
    if (value?.length > 0 && value[0].username && value[value.length - 1].username) {
      return true;
    } else {
      return false;
    }
  };

  const isSongArray = (value: Song[]): value is Song[] => {
    if (value?.length > 0 && value[0].username && value[value.length - 1].username) {
      return true;
    } else {
      return false;
    }
  };

  const fetchSongArray = useCallback(async () => {
    const songArray = await fetchLatestSongs();
    if (songArray?.status === 200 && isSongArray(songArray?.data)) {
      setLatestSongs(songArray.data.reverse());
    } else {
      console.log('No Media Yet', songArray?.error);
      setLatestSongs([]);
    }
  }, []);

  const fetchVideoArray = useCallback(async () => {
    const vidArray = await fetchLatestVideos();

    if (vidArray?.status === 200 && isVideoArray(vidArray?.data)) {
      setLatestVideos(vidArray.data.reverse());
    } else {
      console.log('No Media Yet', vidArray?.error);
      setLatestVideos([]);
    }
  }, []);

  useEffect(() => {
    fetchSongArray();
  }, []);

  useEffect(() => {
    fetchVideoArray();
  }, []);

  useEffect(() => {
    if (state.count === latestVideos.length) {
      setLoadReady(true);
    }
  }, [state]);

  return (
    <PublicDataContext.Provider
      value={{ latestVideos, latestSongs, isSongArray, isVideoArray, dispatch, state, loadReady }}
    >
      {children}
    </PublicDataContext.Provider>
  );
};
